import React, { useState, useEffect } from 'react';
import { HabitCompletionModal } from './HabitCompletionModal';
import { HabitTimerSession } from './HabitTimerSession';
import { HabitStopwatchSession } from './HabitStopwatchSession';
import { HabitSessionReport } from './HabitSessionReport';
import { SessionAttentionCapture } from './SessionAttentionCapture';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { useHabitStore } from '../stores/habitStore';
import type { Habit } from '../types';
import type { SessionFocusCapture } from '../lib/sessionFocusCapture';

interface HabitSessionManagerProps {
  habit: Habit | null;
  onClose: () => void;
}

type SessionView = 'mode-selector' | 'timer' | 'stopwatch' | 'attention' | 'report' | 'minimized' | null;

const formatDuration = (seconds: number) => {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

export function HabitSessionManager({ habit, onClose }: HabitSessionManagerProps) {
  const [currentView, setCurrentView] = useState<SessionView>('mode-selector');
  const [reportData, setReportData] = useState<{
    durationSeconds: number;
    xpEarned: number;
    sessionType: 'timer' | 'stopwatch';
  } | null>(null);
  /** The just-stopped session, held so its rating lands on the right row. */
  const [ratedSessionId, setRatedSessionId] = useState<string | null>(null);

  const { startSession, stopSession, rateSession, activeSession } = useHabitSessionStore();
  const { toggleHabit } = useHabitStore();

  useEffect(() => {
    if (activeSession && habit && activeSession.habitId === habit.id) {
      const sessionType = activeSession.sessionType;
      setCurrentView(sessionType);
    }
  }, [activeSession, habit]);

  if (!habit) return null;

  const handleModeSelected = async (mode: 'quick' | 'timer' | 'stopwatch') => {
    if (mode === 'quick') {
      const today = new Date().toISOString().split('T')[0];
      toggleHabit(habit.id, today);
      onClose();
      return;
    }

    const sessionId = await startSession(habit, mode);
    if (sessionId) {
      setCurrentView(mode);
    }
  };

  const handleSessionComplete = async (durationSeconds: number, xpEarned: number) => {
    const sessionType = activeSession?.sessionType || 'timer';
    // Grab the id BEFORE stopping — `stopSession` clears `activeSession`.
    const sessionId = activeSession?.sessionId ?? null;

    // Stop FIRST. `stopSession` derives duration from `now - startedAt`, so
    // prompting for a rating before this would bill the user's thinking time to
    // the session.
    await stopSession();

    const today = new Date().toISOString().split('T')[0];
    toggleHabit(habit.id, today);

    setReportData({ durationSeconds, xpEarned, sessionType });
    setRatedSessionId(sessionId);
    // No id (a recovered session, say) ⇒ nothing to attach a rating to; skip
    // straight to the report rather than collect a rating that would be dropped.
    setCurrentView(sessionId ? 'attention' : 'report');
  };

  const handleAttentionSubmit = async (focus: SessionFocusCapture) => {
    if (ratedSessionId) await rateSession(ratedSessionId, focus);
    setRatedSessionId(null);
    setCurrentView('report');
  };

  const handleAttentionSkip = () => {
    setRatedSessionId(null);
    setCurrentView('report');
  };

  const handleMinimize = () => {
    setCurrentView('minimized');
    onClose();
  };

  const handleCancel = async () => {
    const { cancelSession } = useHabitSessionStore.getState();
    await cancelSession();
    setCurrentView(null);
    onClose();
  };

  const handleReportClose = () => {
    setReportData(null);
    setCurrentView(null);
    onClose();
  };

  const handleStartAnother = () => {
    setReportData(null);
    setCurrentView('mode-selector');
  };

  if (currentView === 'minimized') {
    return null;
  }

  if (currentView === 'attention' && reportData) {
    return (
      <SessionAttentionCapture
        title={habit.name}
        subtitle={formatDuration(reportData.durationSeconds)}
        onSubmit={handleAttentionSubmit}
        onSkip={handleAttentionSkip}
      />
    );
  }

  if (currentView === 'report' && reportData) {
    return (
      <HabitSessionReport
        habit={habit}
        durationSeconds={reportData.durationSeconds}
        xpEarned={reportData.xpEarned}
        sessionType={reportData.sessionType}
        onClose={handleReportClose}
        onStartAnother={handleStartAnother}
      />
    );
  }

  if (currentView === 'timer') {
    return (
      <HabitTimerSession
        habit={habit}
        onComplete={handleSessionComplete}
        onMinimize={handleMinimize}
        onCancel={handleCancel}
      />
    );
  }

  if (currentView === 'stopwatch') {
    return (
      <HabitStopwatchSession
        habit={habit}
        onComplete={handleSessionComplete}
        onMinimize={handleMinimize}
        onCancel={handleCancel}
      />
    );
  }

  if (currentView === 'mode-selector') {
    return (
      <HabitCompletionModal
        habit={habit}
        onClose={onClose}
        onModeSelected={handleModeSelected}
      />
    );
  }

  return null;
}
